import { Injectable, OnDestroy } from '@angular/core';
import { ApolloQueryResult } from 'apollo-client';
import { gql } from 'graphql-tag';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  Clinic,
  ClinicSetup,
  Doctor,
  FormTemplate,
  QuestionnaireType,
  TemperatureUnit
} from 'src/API';
import { StaffService } from 'src/app/core/api/staff.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { AIScribeUsageData } from 'src/app/shared/consult-forms/assessment-ai-scribe/assessment-ai-scribe.model';
import { TextFavorite, UpdateClinicSetupInput, UpdateClinicSetupMutation } from './../../../API';
import { updateClinicSetup } from './../../../graphql/mutations';
import { getAiScribeUsage } from './../../../graphql/queries';
import { AppSyncService } from './../../core/appsync.service';
import { DryEyeSeverity } from './../../core/remote-json/treatments/dry-eye-category.model';
import {
  FormTemplateSchema,
  FormTemplateSchemaType
} from './../../form-template/form-template-model';
import { AiScribeSetup } from './ai-scribe-setup/ai-scribe-setup.model';
import { InteractionsSetup } from './../interactions-setup-modal/interactions-setup.model';
import { ClinicSetupFormGroup, isDeclinedConsentWarningEnabled } from './clinic-setup-form.model';

export type QuestionnaireScoreConfig = {
  [keys in QuestionnaireType]?: {
    scoreConfig: {
      score: number;
      comparator: '=' | '<' | '>';
      message: DryEyeSeverity;
    }[];
  };
};

export type ReminderSchedule = {
  disabled: boolean;
  name: string;
  friendlyName: string;
};

@Injectable({
  providedIn: 'root'
})
export class ClinicSetupService implements OnDestroy {
  public textFavorites: TextFavorite[];
  public interactionsSetup: InteractionsSetup;
  public aiScribeSetup: AiScribeSetup = {};
  public isClinicAdmin: boolean;
  public clinicOwnerId: string;
  public isSaving: boolean;
  public temperatureInCelsius: string | number;
  public clinicSetupTemperatureUnit: TemperatureUnit;
  public hiddenTreatmentTemplates: string[];
  public clinicStaffs: Doctor[];

  // The saved clinic setup. Settings that other screens react to are derived from this rather than
  // from clinicSetupFormGroup, which also holds unsaved (and possibly discarded) edits.
  private clinicSetupSubject = new BehaviorSubject<ClinicSetup>(null);
  public get clinicSetup(): ClinicSetup {
    return this.clinicSetupSubject.value;
  }
  public set clinicSetup(clinicSetup: ClinicSetup) {
    this.clinicSetupSubject.next(clinicSetup);
  }

  public readonly declinedConsentWarningsEnabled$: Observable<
    boolean
  > = this.clinicSetupSubject.pipe(map(isDeclinedConsentWarningEnabled), distinctUntilChanged());

  public clinicSetupFormGroup = new ClinicSetupFormGroup();

  public clinicSetupId: string;
  public clinicId: string;
  public clinic: Clinic;

  public formTemplates$: Observable<any[]> = this.appStateService.clinic$.pipe(
    untilDestroyed(this),
    map(clinic => {
      return clinic && clinic['clinicFormTemplates']
        ? clinic['clinicFormTemplates'].items.map(formTemplate => {
            return {
              ...formTemplate,
              schema:
                typeof formTemplate.schema === 'string'
                  ? JSON.parse(formTemplate.schema)
                  : formTemplate.schema
            };
          })
        : [];
    })
  );
  public formTemplateKeyToTemplatesMap: {
    [key: string]: (Omit<FormTemplate, 'schema'> & { schema: FormTemplateSchema })[];
  } = Object.keys(FormTemplateSchemaType).reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});

  public formTemplateTypeToTemplatesMap: {
    [key: string]: (Omit<FormTemplate, 'schema'> & { schema: FormTemplateSchema })[];
  };

  public intakeFormTemplates;
  public questionnaireScoreConfig: QuestionnaireScoreConfig;
  constructor(
    private staffService: StaffService,
    private appSyncService: AppSyncService,
    private appStateService: AppStateService
  ) {
    this.formTemplates$.pipe(untilDestroyed(this)).subscribe(formTemplates => {
      this.formTemplateTypeToTemplatesMap = formTemplates.reduce((acc, template) => {
        acc[template.schema.type] = acc[template.schema.type]
          ? [...acc[template.schema.type], template]
          : [template];
        return acc;
      }, {});
    });
  }

  public setClinicSetup(doctor: Doctor) {
    if (doctor && doctor.clinic) {
      this.clinic = doctor.clinic;
      this.clinicId = doctor.clinic.id;
      this.clinicOwnerId = doctor.clinic.owner.id;
      this.isClinicAdmin = this.staffService.isClinicOwner || this.staffService.isClinicAdmin;
      this.clinicStaffs = doctor.clinic.doctors.items;

      if (doctor.clinic.clinicSetup) {
        this.clinicSetupFormGroup.patchValueFromClinicSetup(
          doctor.clinic.clinicSetup,
          doctor.clinic.owner.email
        );

        this.clinicSetup = doctor.clinic.clinicSetup;

        this.clinicSetupId = doctor.clinic.clinicSetup.id;

        const rawInteractions = JSON.parse(doctor.clinic.clinicSetup.interactionsSetup || '{}');
        // Strip legacy AI Scribe keys — they now live in aiScribeSetup
        this.interactionsSetup = {
          types: rawInteractions.types,
          statuses: rawInteractions.statuses,
          communicationMethods: rawInteractions.communicationMethods,
          surgeryTypes: rawInteractions.surgeryTypes
        };

        if (doctor.clinic.clinicSetup.aiScribeSetup) {
          this.aiScribeSetup = JSON.parse(doctor.clinic.clinicSetup.aiScribeSetup);
        } else {
          // Legacy fallback: read from the old interactionsSetup JSON blob.
          // The new field will be written the next time the clinic admin saves
          // any AI Scribe setting from the clinic setup panel.
          this.aiScribeSetup = {
            enabled: rawInteractions.enableAiScribe,
            sections: rawInteractions.aiScribeSections
          };
        }

        this.clinicSetupTemperatureUnit = doctor.clinic.clinicSetup.temperatureUnit;
        this.hiddenTreatmentTemplates = this.clinicSetupFormGroup.controls.hiddenTreatmentTemplates.value;
        this.questionnaireScoreConfig = this.clinicSetupFormGroup.controls.questionnaireScoreConfig.value;
        this.clinicSetup = doctor.clinic.clinicSetup;
      }
    }
  }

  ngOnDestroy(): void {}
  public getReminderSchedule(): ReminderSchedule[] {
    return [
      {
        disabled: false,
        friendlyName: ' 1 Days Before',
        name: '1'
      },
      {
        disabled: false,
        friendlyName: '2 Days Before',
        name: '2'
      },
      {
        disabled: false,
        friendlyName: '3 Days Before',
        name: '3'
      },
      {
        disabled: false,
        friendlyName: '4 Days Before',
        name: '4'
      },
      {
        disabled: false,
        friendlyName: '5 Days Before',
        name: '5'
      },
      {
        disabled: false,
        friendlyName: '6 Days Before',
        name: '6'
      }
    ];
  }

  public getAIScribeUsage(): Observable<AIScribeUsageData> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query({
          query: gql(getAiScribeUsage),
          variables: { clinicId: this.clinicId },
          fetchPolicy: 'no-cache'
        })
      ),
      map((res: any) => {
        const raw = res.data.getAIScribeUsage;
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
      })
    );
  }

  public updateClinicSetup(input: UpdateClinicSetupInput): Observable<ClinicSetup> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client => {
        return client.mutate({
          mutation: gql(updateClinicSetup),
          variables: { input }
        });
      }),
      map((clinicSetupMutation: ApolloQueryResult<UpdateClinicSetupMutation>) => {
        this.clinicSetup = clinicSetupMutation.data.updateClinicSetup;

        return this.clinicSetup;
      })
    );
  }
}
